import React, { forwardRef, useContext, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import Popover from '@mui/material/Popover';
import { ArrowBack, InfoOutlined } from '@mui/icons-material';
import { TroncRow } from '../../../models/tronc';
import { formatValue } from "../../../helpers/helpers";
import { UserContext } from '../../../context/UserContext';
import { UIContext } from '../../../context/UIContext';


export interface DetailsMobileTroncProps {
    onClose: () => void;
    rows?: TroncRow;
    currency: string;

}

const HOURS_KEY = 'hourLegend';
const GRATUITY_KEY = 'gratuityLegend';
const SERVICE_CHANGE_KEY = 'serviceChargeLegend'


const DetailsMobileTronc = forwardRef<HTMLDivElement, DetailsMobileTroncProps>(
    ({ rows, onClose, currency }: DetailsMobileTroncProps, ref) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [currentKey, setCurrentKey] = useState(HOURS_KEY);
    const { locale } = useContext(UserContext);
    const { colorMode } = useContext(UIContext);
    const backgroundColor = theme.palette.background.paper;


    const [anchorEl, setAnchorEl] = useState<SVGSVGElement | null>(null);

    const handleClick = (event: React.MouseEvent<SVGSVGElement>) => {
        setCurrentKey(event.currentTarget.id);
        setAnchorEl(event.currentTarget);

    };

    const handleClose = () => {
        setAnchorEl(null);
    };


    const open = Boolean(anchorEl);
    const id = open ? 'hours-popover' : undefined;

    return (
        <div
    ref={ref}
    tabIndex={-1}
    style={{
        backgroundColor,
        height: '100dvh',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
    }}
>
        <Box
            sx={{
                width: '100%',
                height: '100%',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'center',
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'row', textAlign: 'center', margin: '16px'
 }}>
                <ArrowBack
                    sx={{
                        marginRight: '16px',
                        color: theme?.palette?.greyIcon.main,
                    }}
                    onClick={onClose}
                />
                <Typography variant='h2'>{rows?.staffName}</Typography>
            </Box>
            <Box sx={{ height: '90%', backgroundColor: theme.palette?.background?.default,  borderTop: '1px solid', borderColor: theme.palette.grey['12p'], }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', textAlign: 'center', justifyContent: 'space-between', margin: '16px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} >
                        <Typography variant='boldText'>{t(`screens.troncReport.fields.hours`)}</Typography>
                        <InfoOutlined
                            id={HOURS_KEY}
                            onClick={handleClick}
                            sx={{ marginLeft: '4px', cursor: 'pointer', color: theme?.palette?.greyIcon.main }} />
                    </Box>
                    <Typography variant='body1'>{rows?.hours}</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', textAlign: 'center', justifyContent: 'space-between', margin: '16px', marginTop: '24px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>

                        <Typography variant='boldText'>{t(`screens.troncReport.fields.gratuity`)}</Typography>
                        <InfoOutlined
                            id={GRATUITY_KEY}
                            onClick={handleClick}
                            sx={{ marginLeft: '4px', cursor: 'pointer', color: theme?.palette?.grey?.['54p'] }} />
                    </Box>
                    <Typography variant='body1'>{formatValue(rows?.gratuity, true, currency, locale)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', textAlign: 'center', justifyContent: 'space-between', margin: '16px', marginTop: '24px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} >
                        <Typography variant='boldText'>{t(`screens.troncReport.fields.serviceCharge`)}</Typography>
                        <InfoOutlined
                            id={SERVICE_CHANGE_KEY}
                            onClick={handleClick}
                            sx={{ marginLeft: '4px', cursor: 'pointer', color: theme?.palette?.grey?.['54p'] }} />
                    </Box>
                    <Typography variant='body1'>{formatValue(rows?.serviceCharge, true, currency, locale)}</Typography>
                </Box>
            </Box>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        style: {
                            marginTop: "2px",
                            backgroundColor: colorMode === 'light' ? theme?.palette?.info?.['160p'] : backgroundColor,
                            color: 'white'
                        },
                    },
                }}
            >
                <Typography sx={{ p: 2 }}>{t(`screens.troncReport.fields.${currentKey}`)}</Typography>
            </Popover>
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'row', 
                textAlign: 'center', 
                justifyContent: 'space-between', 
                padding: '16px',
                borderTop: `1px ${colorMode === 'light' ? theme?.palette?.grey?.['12p'] : theme?.palette?.grey?.['23p']
                  } solid`, 
                }}>
                <Typography variant='h2' >{t(`screens.troncReport.fields.totalAmount`)}</Typography>
                <Typography variant='h2'>{formatValue(rows?.totalTronc, true, currency, locale)}</Typography>
            </Box>
        </Box>
        </div>
    );
});
export default DetailsMobileTronc;
